import { formatTextOrDash } from '@/lib/format/text';
import { unitSymbol } from '@/lib/mappers/units';
import { GHG_DEFAULT_VALUE } from '@/config/declarationOptions';
import { escaBonusCapLabel } from '@/config/formOptions';

// The Summary step's three cards, read straight off the form's own values.

const field = (label, value) => ({ label, value: formatTextOrDash(value) });

const declarationColumns = (t, values) => [
  {
    id: 'sd',
    heading: t('SECTION_SUSTAINABILITY_DECLARATION_DETAILS'),
    fields: [
      field(t('ALLOCATIONS_FIELD_SD_NUMBER'), values.idNumber),
      field(t('ALLOCATIONS_FIELD_DATE_OF_ISSUANCE'), values.dateOfIssuance),
      field(t('FIELD_DATE_OF_DISPATCH'), values.dateOfDispatch),
    ],
  },
  {
    id: 'supplier',
    heading: t('SECTION_SUPPLIER'),
    fields: [
      field(t('FIELD_NAME'), values.supplierName),
      field(t('FIELD_ADDRESS'), values.supplierAddress),
      field(t('ALLOCATIONS_FIELD_CERTIFICATE_NUMBER'), values.supplierCertificateNumber),
      field(t('ALLOCATIONS_FIELD_ADDRESS_OF_DISPATCH'), values.supplierDispatchAddress),
    ],
  },
  {
    id: 'recipient',
    heading: t('SECTION_RECIPIENT'),
    fields: [
      field(t('FIELD_NAME'), values.recipientName),
      field(t('FIELD_ADDRESS'), values.recipientAddress),
      field(t('ALLOCATIONS_FIELD_CONTRACT_NUMBER'), values.contractNumber),
      field(t('ALLOCATIONS_FIELD_ADDRESS_OF_RECEIPT'), values.recipientReceiptAddress),
    ],
  },
];

const materialsColumns = (t, values) => [
  {
    id: 'general',
    heading: t('SECTION_GENERAL_INFORMATION'),
    fields: [
      field(t('FIELD_TYPE_OF_PRODUCT'), values.typeOfProduct),
      field(t('FIELD_RAW_MATERIAL_TYPE'), values.rawMaterialType),
      field(t('FIELD_COUNTRY_OF_ORIGIN'), values.countryOfOrigin),
      field(t('ALLOCATIONS_FIELD_TOTAL_CERTIFIED_QUANTITY'), values.totalCertifiedQuantity),
      field(t('FIELD_UNIT'), unitSymbol(values.unit)),
      field(t('ALLOCATIONS_FIELD_ENERGY_CONTENT'), values.energyContent),
    ],
  },
  {
    id: 'compliance',
    heading: t('SECTION_CERTIFICATION_SCOPE'),
    fields: [
      field(t('ALLOCATIONS_FIELD_ISCC_COMPLIANT'), values.isccCompliantMaterial),
      field(t('ALLOCATIONS_FIELD_EU_RED_COMPLIANT'), values.euRedCompliantMaterial),
      field(t('ALLOCATIONS_FIELD_CHAIN_OF_CUSTODY'), values.chainOfCustodyOption),
      field(t('ALLOCATIONS_FIELD_COUNTRY_OF_BIOFUEL'), values.countryOfBiofuelProduction),
      field(
        t('ALLOCATIONS_FIELD_SUSTAINABILITY_CRITERIA'),
        values.sustainabilityCriteriaCompliance,
      ),
      field(t('ALLOCATIONS_FIELD_WASTE_OR_RESIDUE'), values.wasteOrResidue),
    ],
  },
  {
    id: 'support',
    heading: t('SECTION_VOLUNTARY_ADD_ONS'),
    fields: [
      field(t('FORM_FOOD_SECURITY_STANDARD'), values.foodSecurityStandard),
      field(t('FORM_EU_DEFORESTATION_REGULATION'), values.euDeforestationRegulation),
      field(t('FORM_NON_GMO_FOOD_FEED'), values.nonGmoFoodFeed),
      field(t('FORM_NON_GMO_TECHNICAL_MARKETS'), values.nonGmoTechnicalMarkets),
    ],
  },
];

const BREAKDOWN = Object.freeze([
  ['ALLOCATIONS_FIELD_EEC', 'eec'],
  ['ALLOCATIONS_FIELD_EL', 'el'],
  ['ALLOCATIONS_FIELD_EP', 'ep'],
  ['ALLOCATIONS_FIELD_ETD', 'etd'],
  ['ALLOCATIONS_FIELD_EU', 'eu'],
  ['ALLOCATIONS_FIELD_ESCA', 'esca'],
  ['ALLOCATIONS_FIELD_ECCS', 'eccs'],
  ['ALLOCATIONS_FIELD_ECCR', 'eccr'],
]);

const emissionColumns = (t, values) => [
  {
    id: 'ghg',
    heading: t('SECTION_GHG_EMISSION_DETAILS'),
    fields: [
      field(t('ALLOCATIONS_FIELD_DEFAULT_VALUE_APPLIED'), values.defaultValueApplied),
      field(t('ALLOCATIONS_FIELD_TOTAL_GHG_EMISSIONS'), values.totalGhgEmissions),
      // Only the "No" branch collected them, so only it reports them.
      ...(values.defaultValueApplied === GHG_DEFAULT_VALUE.no
        ? BREAKDOWN.map(([key, name]) => field(t(key), values[name]))
        : []),
    ],
  },
  {
    id: 'saving',
    heading: t('SECTION_GHG_EMISSION_SAVING'),
    fields: [
      field(t('ALLOCATIONS_FIELD_SAVING_TRANSPORT'), values.savingTransport),
      field(t('ALLOCATIONS_FIELD_SAVING_ELECTRICITY'), values.savingElectricity),
      field(t('ALLOCATIONS_FIELD_SAVING_OUTERMOST'), values.savingElectricityOutermost),
      field(t('ALLOCATIONS_FIELD_SAVING_USEFUL_HEAT'), values.savingUsefulHeat),
      field(t('ALLOCATIONS_FIELD_SAVING_SUBSTITUTION'), values.savingHeatSubstitution),
    ],
  },
  {
    id: 'allocated',
    heading: t('SECTION_PREFERENCES'),
    fields: [
      field(t('ALLOCATIONS_FIELD_ALLOCATED_HEAT'), values.allocatedHeat),
      field(t('ALLOCATIONS_FIELD_ALLOCATED_ELECTRICITY'), values.allocatedElectricity),
      field(t('ALLOCATIONS_FIELD_ESCA_BONUS_CAP'), escaBonusCapLabel(values.escaBonusCap)),
      field(t('ALLOCATIONS_FIELD_NUTS2_AREA'), values.nuts2Area),
    ],
  },
];

export const buildDeclarationSummarySections = (t, values = {}) => [
  {
    id: 'declaration',
    title: t('ALLOCATIONS_DECLARATION_STEP_DETAILS'),
    columns: declarationColumns(t, values),
  },
  {
    id: 'materials',
    title: t('ALLOCATIONS_DECLARATION_STEP_MATERIALS'),
    columns: materialsColumns(t, values),
  },
  {
    id: 'emissions',
    title: t('ALLOCATIONS_DECLARATION_STEP_EMISSIONS'),
    columns: emissionColumns(t, values),
  },
];
